import { Box, HStack, VStack, Pressable } from 'native-base';
import { isEmpty } from 'lodash';

import { Text, ArrowCircleDownIcon } from '@pimm/base';
import { useAppLocale } from '@pimm/common';
import { ScheduledEmployeeDto } from '@pimm/services/lib/sms-workforce';
import { formatToShortTimeOnly, timeStringToDate } from '@app/utils/date-formatter';
import { formatName } from '@app/utils/string-formatter';

type EmployeeSectionProps = {
  title: string;
  subtitle?: string;
  employees: ScheduledEmployeeDto[];
  employeeId?: string;
  onSelect: (employee: ScheduledEmployeeDto) => void;
};

export const EmployeeSection = ({ title, subtitle, employees, employeeId, onSelect }: EmployeeSectionProps) => {
  const { translate } = useAppLocale();

  return (
    <VStack space={2}>
      <Box>
        <Text fontWeight={600} color="black" size="lg" lineHeight="md">
          {title}
        </Text>
        {subtitle && (
          <Text fontWeight={400} color="black" size="md" lineHeight="md">
            {subtitle}
          </Text>
        )}
      </Box>

      <HStack flexWrap="wrap">
        {employees.map(employee => {
          const isActive = !!employeeId && employee?.employeeId === employeeId;
          const hasShiftTime = employee.shiftStartTime && employee.shiftEndTime;

          return (
            <Box
              key={`${employee?.employeeId}`}
              flex="none"
              p={1}
              w={{ base: '1/2', md: '1/2', lg: '1/3', xl: '1/4', '2xl': hasShiftTime ? '1/4' : '1/5' }}
            >
              <Pressable rounded="lg" onPress={() => onSelect(employee)}>
                {({ isHovered }) => (
                  <VStack
                    space={3}
                    position="relative"
                    rounded="lg"
                    alignItems="center"
                    justifyContent="center"
                    p={2}
                    minH="45px"
                    borderWidth={1}
                    borderColor={isActive ? 'primary.200' : isHovered ? 'gray.400' : 'gray.300'}
                    bg={isActive ? 'primary.50' : isHovered ? 'gray.50' : 'white'}
                  >
                    <HStack w="full" justifyContent="space-between" alignItems="center">
                      <VStack w="3/4">
                        <Text
                          color={isActive ? 'primary.900' : 'black'}
                          fontWeight={700}
                          size="xl"
                          lineHeight="sm"
                          numberOfLines={1}
                          ellipsizeMode="tail"
                        >
                          {formatName(employee?.firstName, employee?.lastName)}
                        </Text>
                        {hasShiftTime && (
                          <Text color={isActive ? 'primary.900' : 'gray.700'} fontWeight={600} size="sm" lineHeight="sm">
                            {`${formatToShortTimeOnly(timeStringToDate(employee.shiftStartTime))} - ${formatToShortTimeOnly(
                              timeStringToDate(employee.shiftEndTime),
                            )}`}
                          </Text>
                        )}
                      </VStack>
                      <Box bg="primary.500" w="30px" minH={7} borderRadius={6} alignItems="center" justifyContent="center">
                        <ArrowCircleDownIcon size="18px" color="black" />
                      </Box>
                    </HStack>
                    {hasShiftTime && !isEmpty(employee.assignedPositions) && (
                      <VStack space={1} w="full">
                        {employee.assignedPositions
                          ?.filter((position, idx, arr) => idx === 0 || position.positionId !== arr[idx - 1].positionId)
                          .map((position, idx) => (
                            <HStack
                              key={idx}
                              alignItems="center"
                              width="full"
                              space={1}
                              justifyContent="space-between"
                              background="gray.25"
                              borderWidth={1}
                              borderColor="gray.100"
                              borderRadius="sm"
                              px={1}
                            >
                              <Text size="sm" fontWeight={500} color="gray.800">
                                {translate(position.positionTitle, position.positionTitleTranslations)}
                              </Text>
                              <Text size="xs" fontWeight={500} color="gray.800" lineHeight="sm">
                                {position.positionStartTime && formatToShortTimeOnly(timeStringToDate(position.positionStartTime))}
                              </Text>
                            </HStack>
                          ))}
                      </VStack>
                    )}
                  </VStack>
                )}
              </Pressable>
            </Box>
          );
        })}
      </HStack>
    </VStack>
  );
};

export default EmployeeSection;
